<template>
  <section class="reviews-page">
    <MainNavbar />
    <div class="container">
    <router-link to="/ipa/thirdparty/seller-dashboard" class="back-to-dashboard">
      <i class="fa-solid fa-arrow-left"></i>
      Back to Dashboard
    </router-link>
    <!-- HEADER -->
    <header class="reviews-header page-header">
      <div class="header-content">
        <h1><i class="fa-solid fa-star"></i> Customer Reviews</h1>
        <p class="subtitle header-subtitle">
          Feedback for <strong class="seller-name-highlight">{{ sellerName }}</strong>
          <span v-if="sellerId" class="seller-id-pill">#{{ sellerId }}</span>
        </p>
        <p class="subtitle-secondary">Ratings and comments from buyers who reviewed your seller profile.</p>
      </div>

      <div class="header-stats">
        <div class="overall-rating">
          <div class="rating-large">
            <span class="rating-number">{{ overallRating }}</span>
            <span class="rating-out-of">/5</span>
          </div>
          <div class="stars-large">
            <i
              v-for="n in 5" 
              :key="n" 
              :class="n <= Math.round(overallRating) ? 'fa-solid fa-star' : 'fa-regular fa-star'"
              :style="{ color: n <= Math.round(overallRating) ? getRatingColor(Math.round(overallRating)) : '#ddd' }"
            ></i>
          </div>
          <p class="total-reviews">{{ totalReviews }} reviews</p>
        </div>
        
        <div class="rating-distribution" aria-label="Rating breakdown">
          <div v-for="star in [5, 4, 3, 2, 1]" :key="star" class="distribution-row">
            <span class="star-count">{{ star }}<i class="fa-solid fa-star"></i></span>
            <div class="distribution-bar">
              <div
                class="bar-fill"
                :style="{ width: ratingPercentForStar(star) + '%' }"
              ></div>
            </div>
            <span class="percentage">{{ ratingPercentForStar(star) }}%</span>
          </div>
        </div>
      </div>
    </header>

    <!-- STATS SUMMARY -->
    <div class="stats-summary summary-stats">
      <div class="stat-card stat-positive">
        <div class="stat-icon">
          <i class="fa-solid fa-thumbs-up"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ positiveReviews }}</div>
          <div class="stat-label">Positive Reviews</div>
        </div>
      </div>

      <div class="stat-card stat-comments">
        <div class="stat-icon">
          <i class="fa-solid fa-comment"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ reviewsWithComments }}</div>
          <div class="stat-label">Reviews with Comments</div>
        </div>
      </div>

      <div class="stat-card stat-recent">
        <div class="stat-icon">
          <i class="fa-solid fa-calendar"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ recentReviews }}</div>
          <div class="stat-label">Last 30 Days</div>
        </div>
      </div>

      <div class="stat-card stat-verified">
        <div class="stat-icon">
          <i class="fa-solid fa-circle-check"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ verifiedPurchases }}</div>
          <div class="stat-label">Verified Purchases</div>
        </div>
      </div>
    </div>

    <!-- FILTERS & CONTROLS -->
    <div class="controls-section">
      <div class="filters-row">
        <div class="search-box">
          <i class="fa-solid fa-search"></i>
          <input 
            type="search" 
            v-model="searchQuery" 
            placeholder="Search by reviewer name or comment…"
            autocomplete="off"
            @input="handleSearch"
          />
        </div>
        
        <div class="filter-group">
          <select v-model="ratingFilter" @change="filterReviews" class="filter-select period-select">
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          
          <select v-model="sortBy" @change="sortReviews" class="filter-select period-select">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
          
          <select v-model="dateFilter" @change="filterReviews" class="filter-select period-select">
            <option value="all">All Time</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
          
          <button type="button" class="refresh-btn" @click="() => fetchReviews({ silent: true })" :disabled="loading">
            <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
        </div>
        <p v-if="searchQuery.trim()" class="search-hint">
          Showing matches in your loaded reviews (up to 100). Clear the search box to return to paged loading.
        </p>
      </div>
    </div>

    <!-- REVIEWS LIST -->
    <div class="reviews-list-container">
      <div v-if="loading" class="loading-reviews">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <p>Loading reviews...</p>
      </div>
      
      <div v-else-if="filteredReviews.length === 0" class="no-reviews">
        <i class="fa-solid fa-comment-slash"></i>
        <h3>No reviews found</h3>
        <p v-if="searchQuery">Try adjusting your search terms</p>
        <p v-else>You don't have any reviews yet</p>
      </div>
      
      <div v-else class="reviews-grid">
        <div 
          v-for="review in paginatedReviews" 
          :key="review.review_id" 
          class="review-card"
          :class="getReviewCardClass(review.rating)"
        >
          <!-- REVIEW HEADER -->
          <div class="review-header">
            <div class="customer-info">
              <div class="customer-avatar">
                {{ getInitials(reviewerDisplayName(review)) }}
              </div>
              <div class="customer-details">
                <h4>{{ reviewerDisplayName(review) }}</h4>
                <div class="review-meta">
                  <span class="date">{{ formatDate(review.created_at) }}</span>
                  <span v-if="review.is_verified_purchase" class="verified-badge">
                    <i class="fa-solid fa-circle-check"></i> Verified Purchase
                  </span>
                </div>
              </div>
            </div>
            
            <div class="review-rating">
              <div class="stars">
                <i 
                  v-for="n in 5" 
                  :key="n" 
                  :class="n <= review.rating ? 'fa-solid fa-star' : 'fa-regular fa-star'"
                  :style="{ color: n <= review.rating ? getRatingColor(review.rating) : '#ddd' }"
                ></i>
              </div>
              <span class="rating-text">{{ review.rating }}/5</span>
            </div>
          </div>
          
          <!-- REVIEW CONTENT -->
          <div class="review-content">
            <h5 v-if="review.title" class="review-title">{{ review.title }}</h5>
            <p class="review-comment">{{ review.comment }}</p>
            
            <!-- PRODUCT INFO (if available) -->
            <div v-if="review.product_name" class="product-info">
              <i class="fa-solid fa-tag"></i>
              <span>Product: {{ review.product_name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- PAGINATION -->
      <div v-if="filteredReviews.length > 0" class="pagination-controls">
        <div class="pagination-info">
          Showing {{ startItem }}–{{ endItem }} of {{ totalItemsDisplay }} reviews
        </div>
        <div class="pagination-buttons">
          <button 
            @click="prevPage" 
            :disabled="currentPage === 1"
            class="pagination-btn"
          >
            <i class="fa-solid fa-chevron-left"></i> Previous
          </button>
          
          <div class="page-numbers" role="navigation" aria-label="Pagination">
            <button 
              v-for="page in visiblePageNumbers" 
              :key="'p-' + page"
              type="button"
              @click="goToPage(page)"
              :class="{ active: page === currentPage }"
              class="page-btn"
            >
              {{ page }}
            </button>
          </div>
          
          <button 
            @click="nextPage" 
            :disabled="currentPage === effectiveTotalPages"
            class="pagination-btn"
          >
            Next <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
        
        <div class="page-size-selector">
          <label>Show:</label>
          <select v-model="pageSize" @change="resetPagination" class="filter-select period-select page-size-select">
            <option value="6">6</option>
            <option value="12">12</option>
            <option value="18">18</option>
            <option value="24">24</option>
          </select>
        </div>
      </div>
    </div>

    </div>
    <!-- Footer outside .container so it spans full viewport width -->
    <div class="reviews-page-footer">
      <Footer />
    </div>
  </section>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import Footer from "./Footer.vue";
import { buildApiUrl } from "@/config/api";
import globalToastMixin from "@/mixins/globalToastMixin";

export default {
  name: "SellerReviews",
  mixins: [globalToastMixin],
  components: { MainNavbar, Footer },
  data() {
    return {
      loading: false,
      allReviews: [],
      filteredReviews: [],
      searchQuery: "",
      ratingFilter: "all",
      sortBy: "newest",
      dateFilter: "all",
      
      // Store the full API response data
      responseData: null,
      
      // Pagination
      currentPage: 1,
      pageSize: 12,
      
      accountReady: false,
      searchTimeout: null
    };
  },
  
  computed: {
    overallRating() {
      if (this.responseData && this.responseData.overall_rating != null) {
        return Number(this.responseData.overall_rating).toFixed(1);
      }
      if (this.allReviews.length === 0) return "0.0";
      const sum = this.allReviews.reduce((total, review) => total + review.rating, 0);
      return (sum / this.allReviews.length).toFixed(1);
    },
    
    totalReviews() {
      return this.responseData?.total_reviews ?? 0;
    },
    
    positiveReviews() {
      return this.responseData?.positive_reviews ?? this.allReviews.filter(r => r.rating >= 4).length;
    },
    
    reviewsWithComments() {
      return this.responseData?.reviews_with_comments ?? this.allReviews.filter(r => r.comment && String(r.comment).trim().length > 0).length;
    },
    
    recentReviews() {
      return this.responseData?.recent_reviews ?? 0;
    },
    
    verifiedPurchases() {
      return this.allReviews.filter(r => r.is_verified_purchase).length;
    },

    /** Raw counts per star (1–5) from API summary or current loaded reviews. */
    ratingDistributionCounts() {
      const dist = this.responseData?.rating_distribution;
      if (dist && typeof dist === "object") {
        return this.normalizeDistributionCounts(dist);
      }
      const out = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      this.allReviews.forEach((review) => {
        if (review.rating >= 1 && review.rating <= 5) {
          out[review.rating] = (out[review.rating] || 0) + 1;
        }
      });
      return out;
    },
    
    sellerName() {
      return this.responseData?.seller_name || "Your Store";
    },
    
    sellerId() {
      return this.responseData?.seller_id || 0;
    },

    totalItemsDisplay() {
      if (this.searchQuery.trim()) {
        return this.filteredReviews.length;
      }
      return this.totalReviews;
    },

    effectiveTotalPages() {
      if (this.searchQuery.trim()) {
        return Math.max(1, Math.ceil(this.filteredReviews.length / this.pageSize) || 1);
      }
      const p = this.responseData?.pagination;
      if (p && p.total_pages != null) {
        return Math.max(1, p.total_pages);
      }
      return Math.max(1, Math.ceil((this.filteredReviews.length || 0) / this.pageSize) || 1);
    },

    visiblePageNumbers() {
      const total = this.effectiveTotalPages;
      const cur = this.currentPage;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      let start = Math.max(1, cur - 3);
      let end = Math.min(total, start + 6);
      start = Math.max(1, end - 6);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    
    startItem() {
      if (this.totalItemsDisplay === 0) return 0;
      if (this.searchQuery.trim()) {
        return (this.currentPage - 1) * this.pageSize + 1;
      }
      const p = this.responseData?.pagination;
      if (p && p.page != null && p.limit != null) {
        return (p.page - 1) * p.limit + 1;
      }
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    
    endItem() {
      if (this.totalItemsDisplay === 0) return 0;
      if (this.searchQuery.trim()) {
        return Math.min(this.currentPage * this.pageSize, this.filteredReviews.length);
      }
      const p = this.responseData?.pagination;
      if (p && p.page != null && p.limit != null) {
        const offset = (p.page - 1) * p.limit;
        return Math.min(offset + this.filteredReviews.length, this.totalReviews);
      }
      const end = this.currentPage * this.pageSize;
      return Math.min(end, this.filteredReviews.length);
    },
    
    paginatedReviews() {
      if (this.searchQuery.trim()) {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredReviews.slice(start, start + this.pageSize);
      }
      return this.filteredReviews;
    }
  },
  
  /**
   * Do not split auth + fetch across async beforeMount + mounted.
   * Vue does not wait for async beforeMount to finish before calling mounted,
   * so accountReady stayed false and fetchReviews never ran.
   */
  async mounted() {
    const ok = await this.ensureSellerAccountAccess();
    this.accountReady = ok;
    if (!ok) return;
    await this.fetchReviews({ silent: true });
  },
  
  methods: {
    // API Methods
    async ensureSellerAccountAccess() {
      const token = localStorage.getItem("access_token") || localStorage.getItem("seller_token");
      if (!token) {
        this.$router.push("/ipa/sign-in");
        return false;
      }
      try {
        const res = await fetch(buildApiUrl("/seller/me"), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) return true;
        localStorage.removeItem("access_token");
        localStorage.removeItem("seller_token");
        this.$router.push("/ipa/sign-in");
        return false;
      } catch {
        this.$router.push("/ipa/sign-in");
        return false;
      }
    },

    async getAuthHeaders() {
      const token = localStorage.getItem("access_token") || localStorage.getItem("seller_token");
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
    },
    
    handleAuthError(res) {
      if (res && (res.status === 401 || res.status === 403)) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("seller_token");
        this.showToast('error', 'Session expired', 'Your session has expired. Please sign in again.');
        this.$router.push('/ipa/sign-in');
        return true;
      }
      return false;
    },
    
    normalizeDistributionCounts(dist) {
      const out = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      if (!dist || typeof dist !== "object") return out;
      for (let s = 1; s <= 5; s++) {
        const v = dist[s] ?? dist[String(s)] ?? 0;
        out[s] = Number(v) || 0;
      }
      return out;
    },

    normalizeReviewRow(r) {
      const name = r.reviewer_name || r.customer_name || "Customer";
      return {
        ...r,
        customer_name: name,
        reviewer_name: r.reviewer_name || name,
      };
    },

    ratingPercentForStar(star) {
      const total = Number(this.totalReviews) || 0;
      if (total <= 0) return 0;
      const counts =
        this.ratingDistributionCounts || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      const raw = counts[star] || 0;
      return Math.round((raw / total) * 100);
    },

    reviewerDisplayName(review) {
      if (!review) return "Customer";
      return review.reviewer_name || review.customer_name || "Customer";
    },

    async fetchReviews(opts = {}) {
      const searchFetch = opts.searchFetch === true;
      this.loading = true;
      try {
        const headers = await this.getAuthHeaders();

        const params = new URLSearchParams();
        params.append("page", String(this.currentPage));

        const limit = searchFetch
          ? 100
          : this.pageSize;
        params.append("limit", String(limit));

        if (this.ratingFilter !== "all") {
          params.append("rating_filter", this.ratingFilter);
        }

        if (this.dateFilter !== "all") {
          params.append("date_filter", this.dateFilter);
        }

        if (this.sortBy) {
          params.append("sort_by", this.sortBy);
        }

        const url = buildApiUrl(`/seller-review/my_reviews?${params.toString()}`);

        // Debug: use the browser DevTools Console (not the terminal). If you see nothing here,
        // `fetch` likely threw before a Response existed — check the catch block and Network tab.
        if (process.env.NODE_ENV === "development") {
          console.info("[SellerReview] GET my_reviews", url);
        }

        let response;
        try {
          response = await fetch(url, {
            method: "GET",
            headers,
          });
        } catch (netErr) {
          console.error("[SellerReview] fetch failed (network/CORS/offline):", netErr);
          throw netErr;
        }

        if (process.env.NODE_ENV === "development") {
          console.info("[SellerReview] response", {
            status: response.status,
            ok: response.ok,
            url: response.url,
          });
        }

        if (this.handleAuthError(response)) return;

        if (response.ok) {
          const data = await response.json();

          if (data && Array.isArray(data.reviews)) {
            this.responseData = data;
            const mapped = data.reviews.map((r) => this.normalizeReviewRow(r));
            this.allReviews = mapped;

            if (searchFetch || this.searchQuery.trim()) {
              this.applySearchFilter();
            } else {
              this.filteredReviews = [...mapped];
            }

            if (data.pagination && data.pagination.page != null && !this.searchQuery.trim()) {
              this.currentPage = data.pagination.page;
            }
          } else {
            this.allReviews = [];
            this.filteredReviews = [];
            this.responseData = null;
            this.showToast("error", "Error", "Invalid response format from server");
          }
        } else {
          let detail = `Failed to fetch reviews (${response.status})`;
          try {
            const err = await response.json();
            if (err.detail) {
              detail = Array.isArray(err.detail)
                ? err.detail.map((d) => d.msg || d).join(" ")
                : String(err.detail);
            }
          } catch {
            /* ignore */
          }
          if (response.status === 404) {
            detail = "Seller account not found. Complete seller signup first.";
          }
          throw new Error(detail);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
        this.showToast("error", "Error", error.message || "Failed to load reviews");
      } finally {
        this.loading = false;
      }
    },

    applySearchFilter() {
      const q = this.searchQuery.toLowerCase().trim();
      if (!q) {
        this.filteredReviews = [...this.allReviews];
        return;
      }
      this.filteredReviews = this.allReviews.filter(
        (r) =>
          (r.reviewer_name && r.reviewer_name.toLowerCase().includes(q)) ||
          (r.customer_name && r.customer_name.toLowerCase().includes(q)) ||
          (r.comment && String(r.comment).toLowerCase().includes(q)) ||
          (r.title && String(r.title).toLowerCase().includes(q))
      );
    },
    
    filterReviews() {
      this.currentPage = 1;
      this.searchQuery = "";
      this.fetchReviews({ silent: true });
    },

    sortReviews() {
      this.currentPage = 1;
      this.searchQuery = "";
      this.fetchReviews({ silent: true });
    },

    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        const q = this.searchQuery.trim();
        if (!q) {
          this.currentPage = 1;
          await this.fetchReviews({ silent: true });
          return;
        }
        this.currentPage = 1;
        await this.fetchReviews({ silent: true, searchFetch: true });
        this.applySearchFilter();
      }, 400);
    },
    
    nextPage() {
      if (this.currentPage >= this.effectiveTotalPages) return;
      this.currentPage++;
      if (!this.searchQuery.trim()) {
        this.fetchReviews({ silent: true });
      }
    },

    prevPage() {
      if (this.currentPage <= 1) return;
      this.currentPage--;
      if (!this.searchQuery.trim()) {
        this.fetchReviews({ silent: true });
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.effectiveTotalPages) return;
      this.currentPage = page;
      if (!this.searchQuery.trim()) {
        this.fetchReviews({ silent: true });
      }
    },

    resetPagination() {
      this.currentPage = 1;
      this.fetchReviews({ silent: true });
    },

    // UI Helper Methods
    getRatingColor(rating) {
      const colors = {
        1: '#ff6b6b',
        2: '#ffa726',
        3: '#ffc107',
        4: '#4ecdc4',
        5: '#54d62c'
      };
      return colors[rating] || '#ffc107';
    },
    
    getReviewCardClass(rating) {
      if (rating >= 4) return 'review-positive';
      if (rating === 3) return 'review-neutral';
      return 'review-negative';
    },
    
    getInitials(name) {
      if (!name) return 'C';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },
    
    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      } catch (error) {
        return 'Unknown date';
      }
    },
    
  }
};
</script>

<style scoped>
.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.95rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}
.back-to-dashboard:hover {
  color: var(--border-color-hover);
}

.reviews-page {
  background: var(--dashboard-bg);
  min-height: 100vh;
  color: var(--text-color1);
  font-family: "Inter", sans-serif;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 24px 40px;
  padding-left: max(24px, env(safe-area-inset-left));
  padding-right: max(24px, env(safe-area-inset-right));
  padding-bottom: max(40px, env(safe-area-inset-bottom));
  box-sizing: border-box;
}

/* Full-bleed footer: not inside .container, so no side gutters from page padding */
.reviews-page-footer {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.seller-name-highlight {
  color: var(--border-color-hover, #0d9488);
  font-weight: 600;
}

.seller-id-pill {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-color2);
  background: var(--background-color3, rgba(0, 0, 0, 0.06));
  border-radius: 999px;
  vertical-align: middle;
}

.subtitle-secondary {
  margin: 0.35rem 0 0 0;
  font-size: 0.9rem;
  color: var(--subtext-color);
  line-height: 1.45;
}

.search-hint {
  margin: 0.75rem 0 0 0;
  font-size: 0.8rem;
  color: var(--subtext-color);
  opacity: 0.95;
}

/* HEADER (aligned with Earnings .page-header) */
.reviews-header.page-header {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.header-content {
  margin-bottom: 1.5rem;
}

.header-content h1 {
  margin: 0 0 8px 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-content h1 i {
  color: var(--border-color-hover);
}

.subtitle {
  color: var(--subtext-color);
  font-size: 1rem;
  margin: 0;
}

.header-subtitle {
  color: var(--subtext-color);
}

.header-stats {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  align-items: center;
}

.overall-rating {
  text-align: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.rating-large {
  margin-bottom: 0.5rem;
}

.rating-number {
  font-size: 3rem;
  font-weight: 800;
  color: var(--heading-color);
  line-height: 1;
}

.rating-out-of {
  font-size: 1.5rem;
  color: var(--subtext-color);
  margin-left: 0.25rem;
}

.stars-large {
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.stars-large i {
  font-size: 1.5rem;
}

.total-reviews {
  color: var(--subtext-color);
  font-size: 0.9rem;
  margin: 0;
}

.rating-distribution {
  padding: 0.5rem;
}

.distribution-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.star-count {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--heading-color);
  font-weight: 500;
  min-width: 40px;
}

.star-count i {
  font-size: 0.8rem;
  color: var(--border-color-hover);
}

.distribution-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--border-color-hover), #10b981);
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.percentage {
  color: var(--subtext-color);
  font-size: 0.9rem;
  min-width: 40px;
  text-align: right;
}

/* STATS SUMMARY (Earnings-style summary-stats) */
.stats-summary.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stats-summary .stat-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 20px;
}

.stats-summary .stat-card:hover {
  transform: translateY(-4px);
  border-color: var(--border-color-hover);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.stats-summary .stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.stat-positive .stat-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.stat-comments .stat-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
}

.stat-recent .stat-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.stat-verified .stat-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.stats-summary .stat-value {
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 4px;
  color: var(--heading-color);
}

.stats-summary .stat-label {
  font-size: 14px;
  color: var(--subtext-color);
  margin: 0;
}

/* CONTROLS SECTION (Earnings card shell) */
.controls-section {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 10px 16px 10px 2.5rem;
  background: var(--background-color2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--text-color1);
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.filter-group {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-select.period-select {
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  min-width: 140px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px;
}

.filter-select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.refresh-btn {
  padding: 10px 20px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 185, 255, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* REVIEWS LIST (Earnings top-products / product-card) */
.reviews-list-container {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.loading-reviews,
.no-reviews {
  padding: 80px 24px;
  text-align: center;
  color: var(--subtext-color);
}

.loading-reviews i {
  font-size: 2.5rem;
  margin-bottom: 16px;
  color: var(--border-color-hover);
}

.no-reviews i {
  font-size: 48px;
  margin-bottom: 16px;
  color: rgba(0, 185, 255, 0.3);
}

.no-reviews h3 {
  color: var(--heading-color);
  margin-bottom: 8px;
  font-size: 1.25rem;
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 20px;
  margin-bottom: 2rem;
}

.review-card {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.review-card:hover {
  border-color: rgba(0, 185, 255, 0.2);
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.review-card.review-positive {
  border-left: 4px solid #54d62c;
}

.review-card.review-neutral {
  border-left: 4px solid #ffc107;
}

.review-card.review-negative {
  border-left: 4px solid #ff6b6b;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.customer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--border-color-hover), #3b82f6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
}

.customer-details h4 {
  color: var(--heading-color);
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
}

.review-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.date {
  color: var(--subtext-color);
  font-size: 0.8rem;
}

.verified-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(84, 214, 44, 0.1);
  color: #54d62c;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
}

.review-rating {
  text-align: right;
}

.stars {
  display: flex;
  gap: 0.125rem;
  margin-bottom: 0.25rem;
}

.rating-text {
  color: var(--subtext-color);
  font-size: 0.9rem;
  font-weight: 500;
}

.review-content {
  margin-bottom: 1.5rem;
}

.review-title {
  color: var(--heading-color);
  font-size: 1.1rem;
  margin: 0 0 0.75rem 0;
}

.review-comment {
  color: var(--subtext-color);
  line-height: 1.6;
  margin-bottom: 1rem;
  white-space: pre-wrap;
}

.product-info {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--background-color3);
  border-radius: 6px;
  color: var(--text-color2);
  font-size: 0.85rem;
}

.product-info i {
  color: var(--primary-color);
}

/* PAGINATION */
.pagination-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  gap: 1rem;
}

.pagination-info {
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--text-color1);
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  background: rgba(0, 185, 255, 0.08);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
}

.page-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--text-color1);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover {
  border-color: var(--border-color-hover);
}

.page-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border-color: var(--border-color-hover);
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-selector label {
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.page-size-select {
  min-width: 72px;
  max-width: 100px;
}

/* TOAST (Earnings / dashboard notification style) */
.toast-notification {
  position: fixed;
  bottom: max(20px, env(safe-area-inset-bottom));
  right: max(20px, env(safe-area-inset-right));
  left: auto;
  background: var(--notification-bg, var(--card-bg));
  border-radius: 16px;
  padding: 20px 48px 20px 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  max-width: 400px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 2000;
  animation: slideInRight 0.35s ease-out;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-notification i {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.toast-notification.success {
  border-left: 4px solid #10b981;
}

.toast-notification.success i {
  color: #10b981;
}

.toast-notification.error {
  border-left: 4px solid #ef4444;
}

.toast-notification.error i {
  color: #ef4444;
}

.toast-notification.warning {
  border-left: 4px solid #f59e0b;
}

.toast-notification.warning i {
  color: #f59e0b;
}

.toast-notification.info {
  border-left: 4px solid var(--border-color-hover);
}

.toast-notification.info i {
  color: var(--border-color-hover);
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  color: var(--heading-color);
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 5px 0;
}

.toast-message {
  color: var(--subtext-color);
  font-size: 0.95rem;
  line-height: 1.45;
  margin: 0;
}

.toast-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--subtext-color);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  background: rgba(255, 255, 255, 0.15);
  color: var(--heading-color);
}

/* ANIMATIONS */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .container {
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }
  
  .header-stats {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .reviews-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .container {
    padding: 80px 16px 32px;
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }

  .reviews-header.page-header,
  .reviews-list-container,
  .controls-section {
    padding: 20px;
  }

  .header-content h1 {
    font-size: 1.5rem;
  }
  
  .stats-summary.summary-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    min-width: 100%;
  }
  
  .filter-group {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .filter-select {
    flex: 1;
    min-width: 120px;
  }
  
  .reviews-grid {
    grid-template-columns: 1fr;
  }
  
  .pagination-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .pagination-buttons {
    justify-content: center;
  }
  
  .page-size-selector {
    justify-content: center;
  }
  
  .toast-notification {
    left: max(16px, env(safe-area-inset-left));
    right: max(16px, env(safe-area-inset-right));
    max-width: none;
    bottom: max(16px, env(safe-area-inset-bottom));
  }
}

@media (max-width: 480px) {
  .reviews-header.page-header,
  .reviews-list-container,
  .controls-section {
    padding: 16px;
  }

  .review-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .review-rating {
    align-self: flex-end;
    text-align: right;
  }
  
  .stats-summary.summary-stats {
    grid-template-columns: 1fr;
  }
  
  .header-content h1 {
    font-size: 1.25rem;
  }
  
  .rating-number {
    font-size: 2.5rem;
  }
  
  .review-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .review-rating {
    text-align: left;
  }
  
  .modal-content {
    margin: 1rem;
    max-height: 85vh;
  }
}
</style>